import { Component, DestroyRef, inject, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { take } from 'rxjs';
import { AlarmRuleService } from '../../../../services/alarm-rule.service';
import { AlarmService } from '../../../../services/alarm.service';
import { DeviceService } from '../../../../services/device.service';
import { LogService } from '../../../../services/log.service';
import { DialogBase } from '../../../../shared/components/dialogs/dialog-base/dialog-base';
import { InputItemSelection } from '../../../../shared/components/input-item-selection/input-item-selection';
import { TableComponent } from '../../../../shared/components/table/table';
import { TranslateSafePipe } from '../../../../shared/pipes/translation.pipe';
import { AlarmRuleDeviceDto, AlarmRuleDeviceRow, AlarmRuleDto } from '../../../../types/dtos/alarm-rule.dto';
import { BaseResponseDto } from '../../../../types/dtos/base.dto';
import { DeviceAlarmRuleResponseDto, DeviceDto } from '../../../../types/dtos/device.dto';
import { InputItemSelectInterface } from '../../../../types/interfaces/input.interface';
import { TableConfig, TableRowDto, TableTagSeverity } from '../../../../types/interfaces/table.interface';
import { DashboardBoxComponent } from '../../../dashboard/dashboard-box/dashboard-box.component';
import { DeviceEditAlarmRulesEdit } from './device-edit-alarm-rules-edit/device-edit-alarm-rules-edit';

@Component({
  selector: 'app-device-edit-alarm-rule',
  imports: [
    DashboardBoxComponent,
    TableComponent,
    DialogBase,
    InputItemSelection,
    DeviceEditAlarmRulesEdit,
    TranslateSafePipe,
  ],
  templateUrl: './device-edit-alarm-rule.html',
  styleUrl: './device-edit-alarm-rule.css',
})
export class DeviceEditAlarmRule {
  public addAlarmRuleDialogOpen: boolean = false;
  public editAlarmRuleDialogOpen: boolean = false;
  public editAlarmRule_Mode: 'view' | 'override' = 'view';
  public editAlarmRule_Id: number | null = null;
  public editAlarmRule_DeviceId: number | null = null;
  public loading: boolean = false;
  public tableRows: TableRowDto<AlarmRuleDeviceRow>[] = [];
  public alarmRuleSelectGroup: FormGroup = new FormGroup({
    selected: new FormControl(),
  });
  public alarmRuleSelect: InputItemSelectInterface = {
    formGroup: this.alarmRuleSelectGroup,
    isTranslated: true,
    label: 'common.action.choose-alarm-rule',
    required: true,
    id: 'selected',
    options: [],
  };
  private _allAlarmRules: AlarmRuleDto[] = [];
  private _alarmRuleList: AlarmRuleDto[] = [];
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _deviceService: DeviceService,
    private readonly _alarmService: AlarmService,
    private readonly _alarmRuleService: AlarmRuleService,
    private readonly _loggingService: LogService,
  ) {
    this._alarmRuleService.alarmRule$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((alarmRules: AlarmRuleDto[]) => {
        this._allAlarmRules = alarmRules;
        this.updateForm();
      });
  }

  private _device: DeviceDto | null = null;

  public tableConfig: TableConfig<AlarmRuleDeviceRow> = {
    columns: [
      {
        key: 'name',
        isTranslated: true,
        title: 'common.table.alarm-rule.label',
      },
      {
        key: 'severity',
        isTranslated: true,
        isValueTranslated: true,
        title: 'common.table.severity.label',
        isTag: true,
        overrideValue: (row: AlarmRuleDeviceRow) => 'common.enum.alarm-severity.' + row.severity,
        getTagColor: (row: AlarmRuleDeviceRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
      },
      {
        key: 'is_active',
        isTranslated: true,
        isValueTranslated: true,
        title: 'common.table.isActive.label',
        isTag: true,
        getTagColor: row => row.is_active ? TableTagSeverity.SUCCESS : TableTagSeverity.DANGER,
        overrideValue: (row: AlarmRuleDeviceRow) => {
          if (row.is_active) return 'common.status.rule-status.active';

          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          return alarmRule ? this._alarmRuleService.disabledAlarmNotice(alarmRule, this._device?.id) : '-';
        },
      },
      {
        key: 'source',
        isTranslated: true,
        isValueTranslated: true,
        title: 'common.table.source.label',
      },
      /*{
       key: 'configuration',
       title: 'Konfiguration',
       },*/
    ],
    mobile: {
      tags: [
        {
          key: 'severity',
          isValueTranslated: true,
          overrideValue: (row: AlarmRuleDeviceRow) => 'common.enum.alarm-severity.' + row.severity,
          getColor: (row: AlarmRuleDeviceRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
        },
      ],
      title: {
        key: 'name',
      },
      sections: [
        {
          key: 'is_active',
          isTranslated: true,
          isValueTranslated: true,
          title: 'common.table.isActive.label',
          sticky: 'left',
          isTag: true,
          getTagColor: row => row.is_active ? TableTagSeverity.SUCCESS : TableTagSeverity.DANGER,
          overrideValue: (row: AlarmRuleDeviceRow) => {
            if (row.is_active) return 'common.status.rule-status.active';

            let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
              .find(alarmRule => alarmRule.id == row.id);

            return alarmRule ? this._alarmRuleService.disabledAlarmNotice(alarmRule, this._device?.id) : '-';
          },
        },
        {
          key: 'source',
          isTranslated: true,
          isValueTranslated: true,
          title: 'common.table.source.label',
        },
        /*{
         key: 'configuration',
         title: 'Konfiguration',
         },*/
      ],
    },
    actions: [
      {
        icon: 'notifications_active',
        tooltip: 'common.actions.tooltip.activate-alarm',
        className: 'green',
        action: (row: AlarmRuleDeviceRow) => {
          this.loading = true;
          this._deviceService.updateDeviceAlarmRule(this._device?.id!, row.id, {is_active: true})
            .pipe(take(1))
            .subscribe({
              next: (result: BaseResponseDto<AlarmRuleDto>) => {
                this._fetch();
              },
              error: (error: any) => {
                this.loading = false;
                console.error('Error adding alarm rule to device:', error);
              },
            });
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          let deviceAlarmRule: AlarmRuleDeviceDto | undefined = alarmRule.devices?.find(device => device.device_id == this._device?.id);
          if (!deviceAlarmRule) return true;

          return deviceAlarmRule.is_active;
        },
      },
      {
        icon: 'notifications_off',
        tooltip: 'common.actions.tooltip.deactivate-alarm',
        className: 'red',
        action: (row: AlarmRuleDeviceRow) => {
          this.loading = true;
          this._deviceService.updateDeviceAlarmRule(this._device?.id!, row.id, {is_active: false})
            .pipe(take(1))
            .subscribe({
              next: (result: BaseResponseDto<AlarmRuleDto>) => {
                this._fetch();
              },
              error: (error: any) => {
                this.loading = false;
                console.error('Error adding alarm rule to device:', error);
              },
            });
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          let deviceAlarmRule: AlarmRuleDeviceDto | undefined = alarmRule.devices?.find(device => device.device_id == this._device?.id);
          if (!deviceAlarmRule) return true;

          return !deviceAlarmRule.is_active;
        },
      },
      {
        icon: 'visibility',
        className: 'green',
        action: (row: AlarmRuleDeviceRow) => {
          this.editAlarmRule_Mode = 'view';
          this.editAlarmRule_Id = row.id;
          this.editAlarmRule_DeviceId = this._device?.id!;

          this.editAlarmRuleDialogOpen = true;
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          return !!alarmRule.devices?.find(device => device.device_id == this._device?.id);
        },
      },
      {
        icon: 'edit',
        className: 'yellow',
        action: (row: AlarmRuleDeviceRow) => {
          this.editAlarmRule_Mode = 'override';
          this.editAlarmRule_Id = row.id;
          this.editAlarmRule_DeviceId = this._device?.id!;

          this.editAlarmRuleDialogOpen = true;
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          return !alarmRule.devices?.find(device => device.device_id == this._device?.id);
        },
      },
      {
        icon: 'remove',
        className: 'red',
        action: (row: AlarmRuleDeviceRow) => {
          this.loading = true;
          this._deviceService.removeAlarmRuleFromDevice(this._device?.id!, row.id)
            .pipe(take(1))
            .subscribe({
              next: (result: BaseResponseDto) => {
                this._fetch();
              },
              error: (error: any) => {
                this.loading = false;
                console.error('Error adding alarm rule to device:', error);
              },
            });
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          return !alarmRule.devices?.some(device => device.device_id == this._device?.id!);
        },
      },
      {
        icon: 'add',
        className: 'green',
        action: (row: AlarmRuleDeviceRow) => {
          this.loading = true;
          this._deviceService.addAlarmRuleToDevice(this._device?.id!, row.id)
            .pipe(take(1))
            .subscribe({
              next: (result: BaseResponseDto) => {
                this._fetch();
              },
              error: (error: any) => {
                this.loading = false;
                console.error('Error adding alarm rule to device:', error);
              },
            });
        },
        hidden: (row: AlarmRuleDeviceRow) => {
          let alarmRule: AlarmRuleDto | undefined = this._alarmRuleList
            .find(alarmRule => alarmRule.id == row.id);

          if (!alarmRule) return true;

          return !!alarmRule.devices?.some(device => device.device_id == this._device?.id!);
        },
      },
    ],
    showRows: 100,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    pagination: false,
    scrollHeight: '55vh',
    emptyMessage: 'common.messages.empty.no-rule-inserted',
    lazy: false,
  };

  @Input()
  set device(value: DeviceDto | null) {
    this._device = value;

    if (!this._device) {
      this.loading = false;
      return;
    }

    this._fetch();
  }

  public updateForm() {
    if (!this._allAlarmRules || !this._alarmRuleList) return;

    this.alarmRuleSelect.options = [];

    this.alarmRuleSelect.options = this._allAlarmRules
      .filter((alarmRule: AlarmRuleDto) => {
        return !this._alarmRuleList.some((allAlarmRule: AlarmRuleDto) => allAlarmRule.id === alarmRule.id);
      })
      .map((alarmRule: AlarmRuleDto) => {
          return {
            label: alarmRule.name,
            value: alarmRule.id!.toString(),
            description: alarmRule.description ?? undefined,
          };
        },
      );
  }

  public onAlarmRuleSelect(value: string): void {
    this.addAlarmRuleDialogOpen = false;

    let selected: number = Number(value);

    if (selected < 1 || !this._device?.id) return;

    this._deviceService.addAlarmRuleToDevice(this._device.id, selected)
      .subscribe({
        next: () => {
          this._fetch();
        },
      });
  }

  private _fetch() {
    if (!this._device) return;

    this.loading = true;

    this._deviceService.getDeviceAlarmRules(this._device.id)
      .subscribe({
        next: (result: DeviceAlarmRuleResponseDto) => {

          this._alarmRuleList = result.data ?? [];
          this.tableRows = (this._alarmRuleList).map((alarmRule: AlarmRuleDto) => {
            let isMappedOnProfile: boolean = alarmRule.deviceProfiles?.some(profile => profile.device_profile_id === this._device?.device_profile_id) ?? false;
            let isMappedOnDevice: boolean = alarmRule.devices?.some(profile => profile.device_id === this._device?.id) ?? false;


            let source: string = '-';
            if (isMappedOnProfile && isMappedOnDevice) source = 'common.status.rule-source.device-x-device-profile';
            else if (isMappedOnProfile) source = 'common.status.rule-source.device-profile';
            else if (isMappedOnDevice) source = 'common.status.rule-source.device';

            return {
              id: alarmRule.id!,
              is_active: this._alarmRuleService.isAlarmActive(alarmRule, this._device?.id!),
              severity: alarmRule.severity,
              name: alarmRule.name,
              source,
              configuration: 'Standard',
            };
          });

          this.updateForm();

        },
        error: (error: unknown) => {
          console.error('Error fetching device measurements:', error);
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

}
